import { createAsyncThunk } from '@reduxjs/toolkit';
import CustomerAPI from 'config/api/CustomerAPI';
import { notify } from 'helpers/NotifyHelper';
import { getDeviceId, getSelectedFflCouponList } from 'helpers/StorageHelper';
import { t } from 'i18next';
import { manageAPICall } from 'store/auth/actions/CommonCrudAction';

// Returned by the update quantity API when the cart item id no longer exists. It is a code rather
// than a translated sentence precisely so the client can recover from it - see updateCartQtyAPI.
export const CART_ITEM_ID_INVALID = 'cart_item_id_invalid';

const isSingleCartType = (payload: any): boolean =>
  Boolean(
    payload?.is_buy_now || payload?.cart_type === 'buy_now' || payload?.cart_type === 'single'
  );

// A quantity change or removal moves the quoted coupon discount (and can drop it below the
// coupon's minimum), so the list is pulled again whenever a coupon is in play.
const hasCartCoupons = (payload: any): boolean =>
  !isSingleCartType(payload) && getSelectedFflCouponList().length > 0;

// ..................ASYNC THUNKS....................
// Get Cart
export const getCartListAPI = createAsyncThunk(
  'cart/fetch',
  async (payload: any, { rejectWithValue }) => {
    try {
      // _silentRefresh is read from the action meta by the slice (no spinner, keep the
      // offer notice) - it must not travel to the API, so drop it here.
      const { _silentRefresh, ...rest } = payload || {};
      const apiPayload: any = { ...rest, session_id: getDeviceId() };
      // The cart page shows the coupon each FFL's items were picked with on the product page, so
      // the API is told which ones to quote. Buy Now carts go straight to checkout and skip this.
      if (!isSingleCartType(rest)) {
        const coupons = getSelectedFflCouponList();
        if (coupons.length) apiPayload.coupons = coupons;
      }
      const res: any = await manageAPICall(
        CustomerAPI.cartList.url,
        CustomerAPI.cartList.method,
        apiPayload
      );
      if (res?.status === false) {
        notify.error(res.message);
        return rejectWithValue(res);
      }
      // The API drops lines whose product sold out or was taken down - tell the customer which, from
      // here so it shows whichever screen asked for the cart (cart page, mini cart, checkout...)
      const unavailableItems = res?.data?.unavailable_items;
      if (Array.isArray(unavailableItems) && unavailableItems.length > 0) {
        notify.info(t('cart_items_unavailable_removed', { names: unavailableItems.join(', ') }));
      }
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
// Add Item
export const addToCartAPI = createAsyncThunk(
  'cart/add',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res: any = await manageAPICall(
        CustomerAPI.addToCart.url,
        CustomerAPI.addToCart.method,
        payload
      );
      if (res?.status === false) {
        notify.error(res.message);
        return rejectWithValue(res);
      }
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Update Quantity
export const updateCartQtyAPI = createAsyncThunk(
  'cart/update',
  async (payload: any, { rejectWithValue, dispatch }) => {
    const { product_id, ...updatePayload } = payload || {};
    const apiPayload = { ...updatePayload, session_id: getDeviceId() };
    try {
      const res: any = await manageAPICall(
        CustomerAPI.updateCartQty.url,
        CustomerAPI.updateCartQty.method,
        apiPayload
      );
      if (res?.status === false) {
        // The line the stepper was pointing at is gone - the cart was cleared in another tab, the
        // session was migrated on login, or the item was removed elsewhere. The customer only ever
        // asked for a quantity, so put the product back with the quantity they clicked instead of
        // showing them an error they cannot act on.
        if (res?.message === CART_ITEM_ID_INVALID && product_id) {
          const addRes: any = await dispatch(
            addToCartAPI({
              product_id,
              quantity: payload?.quantity,
              ...(payload?.cart_type ? { cart_type: payload.cart_type } : {}),
              ...(payload?.is_buy_now ? { is_buy_now: payload.is_buy_now } : {}),
              session_id: getDeviceId(),
            })
          );
          if (addRes?.meta?.requestStatus === 'fulfilled') {
            // Same reason as the refetch below - adding re-syncs offers across the whole cart.
            if ((addRes.payload?.data?.offer_changes || []).length > 0) {
              dispatch(getCartListAPI({ cart_type: payload?.cart_type, _silentRefresh: true }));
            }
            return addRes.payload;
          }
          // addToCartAPI has already reported its own failure.
          return rejectWithValue(res);
        }
        notify.error(res.message);
        return rejectWithValue(res);
      }

      // The API re-syncs accepted offers across the whole cart, so a line other than
      // the one just changed may have been re-priced. Only that line comes back in the
      // response, so pull the full list again to keep the rows and the totals in step.
      if ((res?.data?.offer_changes || []).length > 0 || hasCartCoupons(payload)) {
        dispatch(getCartListAPI({ cart_type: payload?.cart_type, _silentRefresh: true }));
      }

      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Remove Item
export const removeCartItemAPI = createAsyncThunk(
  'cart/remove',
  async (payload: any, { rejectWithValue, dispatch }) => {
    const itemId =
      typeof payload === 'object' && payload !== null
        ? payload.itemId || payload.cart_item_id || payload.id
        : payload;
    const cartType =
      typeof payload === 'object' && payload !== null ? payload.cart_type : undefined;
    const apiPayload: any = { cart_item_id: itemId, session_id: getDeviceId() };
    if (cartType) apiPayload.cart_type = cartType;
    try {
      const res: any = await manageAPICall(
        CustomerAPI.deleteItemFromCart.url,
        CustomerAPI.deleteItemFromCart.method,
        apiPayload
      );
      if (res?.status === false) {
        notify.error(res.message);
        return rejectWithValue(res);
      }
      if (hasCartCoupons(payload)) {
        dispatch(getCartListAPI({ cart_type: cartType, _silentRefresh: true }));
      }
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
